"use client";

import {useEffect, useRef, useState} from "react";
import {AnimatePresence, motion, useInView, useReducedMotion} from "motion/react";
import {useTranslation} from "react-i18next";
import useScreenSize from "@/hooks/useScreenSize";
import {GAIL_ASSISTANT_CONTENT_TRANSITION} from "@/utils/chat/gailGptLauncher";

const TIP_INTERVAL_MS = 8000;
/** The outgoing tip clears quickly; the incoming one starts a beat later. */
const TIP_EXIT = {...GAIL_ASSISTANT_CONTENT_TRANSITION, duration: 0.2};
const TIP_ENTER = {...GAIL_ASSISTANT_CONTENT_TRANSITION, duration: 0.3, delay: 0.06};
/** Tips drift up through a light blur; reduced motion keeps only the fade. */
const TIP_MOTION = {
   enter: {opacity: 0, transform: "translateY(6px)", filter: "blur(2px)"},
   rest: {opacity: 1, transform: "translateY(0px)", filter: "blur(0px)"},
   exit: {opacity: 0, transform: "translateY(-6px)", filter: "blur(2px)"},
};
const TIP_FADE = {enter: {opacity: 0}, rest: {opacity: 1}, exit: {opacity: 0}};

/** Discoverable capabilities in the empty sidebar, with space reserved for every tip. */
export default function GailWelcomeTips({canUpload}: {canUpload: boolean}) {
   const {t} = useTranslation("ask");
   const {isMobile} = useScreenSize();
   const reducedMotion = useReducedMotion();
   const ref = useRef<HTMLDivElement>(null);
   const visible = useInView(ref, {amount: 0.9});
   const [index, setIndex] = useState(0);
   const keys = [
      "docs", "calls", "scripts", "workflows",
      ...(canUpload ? ["files"] : []),
      "dictation",
      ...(!isMobile ? ["resize"] : []),
   ];
   const tipCount = keys.length;

   useEffect(() => {
      if (!visible) return;
      const timer = window.setInterval(() => {
         if (!document.hidden) setIndex((current) => (current + 1) % tipCount);
      }, TIP_INTERVAL_MS);
      return () => window.clearInterval(timer);
   }, [visible, tipCount]);

   const activeKey = keys[index % tipCount];
   const tipMotion = reducedMotion ? TIP_FADE : TIP_MOTION;

   return (
      <div className="flex w-full max-w-[20rem] flex-col items-center gap-1">
         <div
            ref={ref}
            className="grid w-full max-w-[20rem] text-xs leading-5 text-600"
            aria-live="off"
         >
            {/* Overlapping invisible copies reserve the tallest translated tip's height. */}
            {keys.map((key) => (
               <p key={key} aria-hidden="true" className="invisible col-start-1 row-start-1">
                  {t(`launcher.tips.${key}`)}
               </p>
            ))}
            {/* Crossfade in the shared cell; the blur keeps the brief overlap from reading as two sentences. */}
            <AnimatePresence initial={false}>
               <motion.p
                  key={activeKey}
                  initial={tipMotion.enter}
                  animate={{...tipMotion.rest, transition: TIP_ENTER}}
                  exit={{...tipMotion.exit, transition: TIP_EXIT}}
                  className="col-start-1 row-start-1"
               >
                  {t(`launcher.tips.${activeKey}`)}
               </motion.p>
            </AnimatePresence>
         </div>
      </div>
   );
}
